// Slice H2 — Donchian Channel Breakout (curated template v1).
//
// Week 2 A2 rewrite — proper Donchian upper/lower band exposure via
// CUSTOM blocks. See `canonicalIndicatorBands.ts` header for the full
// architectural rationale. tl;dr: the engine's built-in `volatility`
// block exposes only the middle of the Donchian channel; A2 replaces
// the bare volatility block with CUSTOM `indicator` blocks that call
// `helpers.donchianChannels(candles, period).upper` (and `.lower`)
// directly + expose those as the blocks' output series.
//
// Strategy logic (now actually matches the documentation):
//   • Donchian channel (20-bar): upper = highest high(20), lower = lowest low(20).
//   • Enter long when close > UPPER band (confirmed breakout).
//   • Exit on touch of the LOWER band (trend reversal).
//   • Wider 3% stop, 1:2.67 risk-reward TP.
//   • Per-trade risk capped at 1%.

import { BlockType, type Block } from '../types';
import {
  type CuratedTemplate,
  templateBlockMetadata,
  namespacedBlockId,
} from './types';
import {
  maxRiskPerTradeParameters,
} from './canonicalRiskRuleBodies';
import {
  CANONICAL_DONCHIAN_UPPER_TSL,
  CANONICAL_DONCHIAN_LOWER_TSL,
} from './canonicalIndicatorBands';

const SLUG = 'breakout-donchian';
const id = (name: string) => namespacedBlockId(SLUG, name);
const meta = (desc: string) => templateBlockMetadata(desc);

const blocks: Block[] = [
  {
    id: id('config'),
    type: BlockType.STRATEGY_CONFIG,
    data: {
      name: 'Strategy config',
      initial_capital: 10_000,
      commission_value: 0.06,
      commission_type: 'percentage',
      slippage_value: 0.02,
      slippage_type: 'percentage',
      // S65 (2026-06-06) — normalized to false. Matches the engine +
      // converter default flipped in S65. Users who want same-bar-close
      // fills can tick it back on in the cfg block.
      process_orders_on_close: false,
    } as Block['data'],
    position: { x: 50, y: 320 },
    connections: { inputs: [], outputs: [] },
    metadata: meta('Capital + costs for crypto-futures breakout trading.'),
  },

  {
    id: id('price'),
    type: BlockType.PRICE_DATA,
    data: { name: 'Price data' } as Block['data'],
    position: { x: 50, y: 80 },
    connections: { inputs: [], outputs: [] },
    metadata: meta('Source candle feed.'),
  },

  // ── Donchian UPPER band via CUSTOM indicator block (A2 fix) ──────
  // Calls helpers.donchianChannels(candles, 20).upper internally.
  // The expression compares close vs this value to detect genuine
  // breakouts above the highest high of the prior `period` bars.
  {
    id: id('donchian-upper'),
    type: BlockType.CUSTOM,
    data: {
      name: 'Donchian UPPER (highest high 20)',
      blockType: 'indicator',
      tslAst: CANONICAL_DONCHIAN_UPPER_TSL,
      parameters: { period: 20 },
      // S4d (2026-06-02): `outputTypes` removed (dead field). See audit doc.
    } as Block['data'],
    position: { x: 230, y: 40 },
    connections: { inputs: [], outputs: [id('expr-close-above-upper')] },
    metadata: meta('CUSTOM indicator — true upper band, not midline proxy.'),
  },

  // ── Donchian LOWER band — exit trigger ───────────────────────────
  {
    id: id('donchian-lower'),
    type: BlockType.CUSTOM,
    data: {
      name: 'Donchian LOWER (lowest low 20)',
      blockType: 'indicator',
      tslAst: CANONICAL_DONCHIAN_LOWER_TSL,
      parameters: { period: 20 },
      // S4d (2026-06-02): `outputTypes` removed (dead field). See audit doc.
    } as Block['data'],
    position: { x: 230, y: 110 },
    connections: { inputs: [], outputs: [id('exit-on-lower')] },
    metadata: meta('CUSTOM indicator — true lower band for exit signal.'),
  },

  // ── Entry condition: close above genuine upper band ─────────────
  {
    id: id('expr-close-above-upper'),
    type: BlockType.EXPRESSION,
    data: {
      name: 'Close > Upper Band (genuine breakout)',
      leftSource: 'price',
      leftPriceSource: 'close',
      leftPriceDataBlockId: id('price'),
      rightSource: 'input',
      rightInputBlockId: id('donchian-upper'),
      operator: '>',
    } as Block['data'],
    position: { x: 430, y: 40 },
    connections: { inputs: [id('donchian-upper')], outputs: [id('entry')] },
    metadata: meta('Confirmed breakout = close above the genuine upper envelope.'),
  },

  // ── Terminals ────────────────────────────────────────────────────
  {
    id: id('entry'),
    type: BlockType.ENTRY,
    data: {
      name: 'Long entry (breakout)',
      direction: 'long',
      entryType: 'market',
      logic: 'AND',
      useStrategyDefaults: false,
    } as Block['data'],
    position: { x: 630, y: 40 },
    connections: { inputs: [id('expr-close-above-upper')], outputs: [] },
    metadata: meta('Market entry on genuine breakout confirmation.'),
  },

  {
    id: id('exit-on-lower'),
    type: BlockType.EXIT,
    data: {
      name: 'Exit on lower-band touch',
    } as Block['data'],
    position: { x: 630, y: 110 },
    connections: { inputs: [id('donchian-lower')], outputs: [] },
    metadata: meta('Trend break — close on touch of the genuine lower envelope.'),
  },

  {
    id: id('sl'),
    type: BlockType.STOP_LOSS,
    data: {
      name: 'Stop loss (3%)',
      stopType: 'percentage',
      stopValue: 3.0,
      // S4c (2026-06-02): `executionMethod` removed (dead field). See audit doc.
      exitMode: 'full',
    } as Block['data'],
    position: { x: 630, y: 280 },
    connections: { inputs: [], outputs: [] },
    metadata: meta('Wider 3% stop — breakouts retrace before they run.'),
  },

  {
    id: id('tp'),
    type: BlockType.TAKE_PROFIT,
    data: {
      name: 'Take profit (1:2.67 risk-reward)',
      tpType: 'risk_reward',
      riskRewardRatio: 2.67,
      // S4c (2026-06-02): `executionMethod` removed (dead field). See audit doc.
    } as Block['data'],
    position: { x: 630, y: 360 },
    connections: { inputs: [], outputs: [] },
    metadata: meta('Breakouts have positive return skew — wider TP.'),
  },

  // ── Risk control ─────────────────────────────────────────────────
  {
    id: id('risk-max-per-trade'),
    type: BlockType.CUSTOM,
    data: {
      name: 'Max risk per trade (1%)',
      blockType: 'risk_rule',
      parameters: maxRiskPerTradeParameters(1.0, 'resize'),
      // S4d (2026-06-02): `outputTypes` removed (dead field). See audit doc.
    } as Block['data'],
    position: { x: 50, y: 480 },
    connections: { inputs: [], outputs: [] },
    metadata: meta('Per-trade risk capped at 1% (canonical).'),
  },
];

export const BREAKOUT_DONCHIAN: CuratedTemplate = {
  slug: SLUG,
  name: 'Donchian Breakout (v1)',
  family: 'breakout',
  bestFor: 'Enters on confirmed breakouts above the 20-bar Donchian UPPER band.',
  detail:
    'A volatility-expansion play. Waits for a close above the highest high of the prior ' +
    '20 bars (the Donchian upper band), then enters long. Exits on touch of the lower band ' +
    '(trend reversal). Wider stops (3%) and TPs (8% via 1:2.67 ratio) reflect breakout ' +
    'return-skew — most signals chop out, but the winners run further than the losers.',
  assetGuidance:
    'Crypto futures (BTC, ETH perps). Also works on commodity daily charts. Avoid range-bound ' +
    'periods — the upper/lower band breaches will whipsaw.',
  timeframeGuidance: ['15m', '1h', '4h'],
  riskNotes: [
    'High paramStability CV — winning streaks cluster around volatility expansions',
    'False breakouts are the dominant failure mode',
    'Wider stops mean larger position-sizing under risk-per-trade rule',
  ],
  expectedValidation:
    'Green possible on volatility-expansion samples (post-consolidation breakouts); yellow ' +
    'on mixed regimes; red+gate_failed if drawdown gate trips during a false-breakout cluster.',
  blocks,
  version: 'v1',
};
